import { UserDto } from '@shared/contracts';
import { RefreshCw, UserPlus, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';

export const UserManagementPage: React.FC = () => {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'SUPER_ADMIN' | 'USER'>('USER');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const [usersList, setUsersList] = useState<UserDto[]>([
    { id: 'ygzor0oy91ci', sub: 'ygzor0oy91ci', email: 'dev.mohammadii@gmail.com', role: 'SUPER_ADMIN', tenantId: 'tenant-default-001' },
  ]);

  const loadUsers = async () => {
    setFetching(true);
    try {
      const data = await authApi.getUsers();
      if (Array.isArray(data) && data.length > 0) {
        setUsersList(data);
      }
    } catch (e) {
      console.warn('Failed to load users from backend, using fallback list', e);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      const createdUser = await authApi.register({ email, role });
      setUsersList((prev) => [...prev, createdUser]);
      setMessage(`User ${email} registered successfully as ${role}!`);
      setEmail('');
    } catch (err) {
      const error = err as Error;
      setMessage('Error: ' + (error.message || 'Provisioning user failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: 'SUPER_ADMIN' | 'USER') => {
    try {
      const updated = await authApi.updateUserRole(userId, newRole);
      setUsersList((prev) => prev.map((u) => (u.id === userId ? { ...u, role: updated.role } : u)));
      setMessage(`Updated role for user ${userId} to ${newRole}`);
    } catch (err) {
      const error = err as Error;
      setMessage('Role Update Error: ' + error.message);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto font-sans">
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-sky-600" />
            <span>User Directory & Role Access</span>
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Super Admin Portal: Manage user roles strictly between <span className="font-semibold text-slate-700">SUPER_ADMIN</span> and <span className="font-semibold text-slate-700">USER</span>.
          </p>
        </div>

        <button
          onClick={loadUsers}
          disabled={fetching}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 text-xs font-semibold transition-all"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${fetching ? 'animate-spin text-sky-600' : ''}`} />
          <span>Refresh List</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Provision User Form */}
        <div className="lg:col-span-5 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
          <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-sky-600" />
            <span>Provision User Account</span>
          </h2>

          {message && (
            <div className="p-3 rounded-xl bg-slate-900 text-white text-xs font-medium border border-slate-800">
              {message}
            </div>
          )}

          <form onSubmit={handleCreateUser} className="space-y-3.5">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Email Address</label>
              <input
                type="email"
                required
                placeholder="user@organization.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-xl px-3.5 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">System Access Level</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as 'SUPER_ADMIN' | 'USER')}
                className="w-full bg-white border border-slate-300 rounded-xl px-3.5 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500 font-semibold"
              >
                <option value="SUPER_ADMIN">👑 SUPER_ADMIN (Full Platform Access)</option>
                <option value="USER">🚫 USER (Regular User - Restricted)</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold shadow-sm transition-all"
            >
              {loading ? 'Provisioning...' : 'Provision User'}
            </button>
          </form>
        </div>

        {/* Users Table */}
        <div className="lg:col-span-7 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-900">User Directory</h2>
            <span className="text-xs text-slate-500 font-semibold">{usersList.length} Accounts</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-700 min-w-[500px]">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px] font-semibold border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-3 whitespace-nowrap">Email</th>
                  <th className="py-2.5 px-3 whitespace-nowrap">Assigned Role</th>
                  <th className="py-2.5 px-3 whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {usersList.map((u) => {
                  const isSuper = u.role === 'SUPER_ADMIN';
                  return (
                    <tr key={u.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-2.5 px-3 whitespace-nowrap">
                        <div className="font-semibold text-slate-900">{u.email}</div>
                        <div className="text-[10px] text-slate-400 font-mono">{u.id}</div>
                      </td>
                      <td className="py-2.5 px-3 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${isSuper
                              ? 'bg-amber-100 text-amber-800 border border-amber-300'
                              : 'bg-red-50 text-red-700 border border-red-200'
                            }`}
                        >
                          {isSuper ? '👑 SUPER_ADMIN' : '🚫 USER (Restricted)'}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 whitespace-nowrap">
                        <select
                          value={u.role}
                          onChange={(e) => handleRoleChange(u.id, e.target.value as 'SUPER_ADMIN' | 'USER')}
                          className="bg-slate-50 border border-slate-300 text-slate-800 text-[11px] rounded-lg px-2 py-1 font-semibold focus:outline-none focus:ring-1 focus:ring-sky-500"
                        >
                          <option value="SUPER_ADMIN">Make Super Admin</option>
                          <option value="USER">Set Regular User</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
